import { useCallback, useEffect, useState } from "react";
import Badge from "../components/Badge";
import Button from "../components/Button";
import Card from "../components/Card";
import Dialog from "../components/Dialog";
import Panel from "../components/Panel";
import Spinner from "../components/Spinner";
import Tabs from "../components/Tabs";
import TextInput from "../components/TextInput";
import { useToast } from "../components/ToastProvider";
import type { Category, CategoryType } from "../hooks/useClassification";
import { useClassification } from "../hooks/useClassification";
import styles from "./Classification.module.css";

const TABS = [
  { id: "technology", label: "Technology categories" },
  { id: "application", label: "Application categories" },
];

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE_HINT = "category";

/**
 * The Classification blade. Manages the TechnologyCategory and
 * ApplicationCategory term-holder nodes in DataHub via the BFF. Every
 * authenticated user can read; write controls render only for a user the BFF
 * reports as able to manage (the admin group — ADR 0004). The BFF re-checks on
 * every write, so hiding the controls is a convenience, not the control.
 */
function Classification() {
  const { getCapability, list, create, rename, remove } = useClassification();
  const { notify } = useToast();

  const [tab, setTab] = useState<CategoryType>("technology");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const [items, setItems] = useState<Category[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [canManage, setCanManage] = useState(false);

  // Edit dialog: `editing` null means create, non-null means rename.
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [draftName, setDraftName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);
  const [busy, setBusy] = useState(false);

  // Debounce the search box so typing does not fire a request per keystroke.
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query]);

  // Resolve write capability once. A failure here degrades to read-only rather
  // than blocking the view.
  useEffect(() => {
    let cancelled = false;
    getCapability()
      .then((cap) => {
        if (!cancelled) setCanManage(cap.can_manage);
      })
      .catch(() => {
        if (!cancelled) setCanManage(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getCapability]);

  const loadFirstPage = useCallback(
    (signal: { cancelled: boolean }) => {
      setLoading(true);
      setError(null);
      list(tab, debouncedQuery ? { name: debouncedQuery } : {})
        .then((page) => {
          if (signal.cancelled) return;
          setItems(page.data);
          setNextCursor(page.pagination.next);
        })
        .catch((cause: unknown) => {
          if (signal.cancelled) return;
          setError(cause instanceof Error ? cause.message : "Could not load categories.");
          setItems([]);
          setNextCursor(null);
        })
        .finally(() => {
          if (!signal.cancelled) setLoading(false);
        });
    },
    [list, tab, debouncedQuery],
  );

  // Reload whenever the active tab or the debounced search term changes.
  useEffect(() => {
    const signal = { cancelled: false };
    loadFirstPage(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [loadFirstPage]);

  const reload = useCallback(() => loadFirstPage({ cancelled: false }), [loadFirstPage]);

  const loadMore = useCallback(() => {
    if (!nextCursor) return;
    list(tab, { name: debouncedQuery || undefined, cursor: nextCursor })
      .then((page) => {
        setItems((current) => [...current, ...page.data]);
        setNextCursor(page.pagination.next);
      })
      .catch((cause: unknown) => {
        notify(cause instanceof Error ? cause.message : "Could not load more.", "error");
      });
  }, [list, tab, debouncedQuery, nextCursor, notify]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setDraftName("");
    setEditOpen(true);
  }, []);

  const openRename = useCallback((category: Category) => {
    setEditing(category);
    setDraftName(category.name);
    setEditOpen(true);
  }, []);

  const closeEdit = useCallback(() => {
    if (!busy) setEditOpen(false);
  }, [busy]);

  const submitEdit = useCallback(() => {
    const name = draftName.trim();
    if (!name) return;
    setBusy(true);
    const action = editing
      ? rename(tab, editing.id, name).then(() => `Renamed to “${name}”.`)
      : create(tab, name).then(() => `Created “${name}”.`);
    action
      .then((message) => {
        notify(message, "success");
        setEditOpen(false);
        reload();
      })
      .catch((cause: unknown) => {
        notify(cause instanceof Error ? cause.message : "Save failed.", "error");
      })
      .finally(() => setBusy(false));
  }, [draftName, editing, rename, create, tab, notify, reload]);

  const confirmDelete = useCallback(() => {
    if (!deleteTarget) return;
    setBusy(true);
    remove(tab, deleteTarget.id)
      .then(() => {
        notify(`Deleted “${deleteTarget.name}”.`, "success");
        setDeleteTarget(null);
        reload();
      })
      .catch((cause: unknown) => {
        notify(cause instanceof Error ? cause.message : "Delete failed.", "error");
      })
      .finally(() => setBusy(false));
  }, [deleteTarget, remove, tab, notify, reload]);

  const noun = tab === "technology" ? "technology" : "application";

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Classification</h1>
          <p className={styles.subtitle}>
            Manage the category terms applied to technologies and applications.
          </p>
        </div>
        {canManage && (
          <Button variant="add" onClick={openCreate}>
            + New category
          </Button>
        )}
      </header>

      <Tabs
        items={TABS}
        activeId={tab}
        onChange={(id) => setTab(id as CategoryType)}
      />

      <div className={styles.toolbar}>
        <TextInput
          className={styles.search}
          type="search"
          placeholder={`Search ${noun} categories by name…`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={`Search ${noun} categories`}
        />
        {!canManage && <Badge variant="neutral">Read-only</Badge>}
      </div>

      <Panel>
        {loading ? (
          <div className={styles.state}>
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className={styles.state}>
            <p className={styles.errorText}>{error}</p>
            <Button onClick={reload}>Try again</Button>
          </div>
        ) : items.length === 0 ? (
          <div className={styles.state}>
            <p className={styles.emptyText}>
              {debouncedQuery
                ? `No ${noun} categories match “${debouncedQuery}”.`
                : `No ${noun} categories yet.`}
            </p>
            {canManage && !debouncedQuery && (
              <Button variant="add" onClick={openCreate}>
                + New category
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className={styles.listMeta}>
              <span className={styles.count}>
                {items.length} {items.length === 1 ? PAGE_SIZE_HINT : "categories"}
                {nextCursor ? "+" : ""}
              </span>
            </div>
            <ul className={styles.list}>
              {items.map((category) => (
                <li key={category.id}>
                  <Card className={styles.row}>
                    <span className={styles.name}>{category.name}</span>
                    {canManage && (
                      <div className={styles.actions}>
                        <Button variant="tool" onClick={() => openRename(category)}>
                          Rename
                        </Button>
                        <Button variant="tool" onClick={() => setDeleteTarget(category)}>
                          Delete
                        </Button>
                      </div>
                    )}
                  </Card>
                </li>
              ))}
            </ul>
            {nextCursor && (
              <div className={styles.loadMore}>
                <Button onClick={loadMore}>Load more</Button>
              </div>
            )}
          </>
        )}
      </Panel>

      <Dialog
        open={editOpen}
        title={editing ? "Rename category" : "New category"}
        onClose={closeEdit}
        footer={
          <>
            <Button onClick={closeEdit} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitEdit}
              disabled={busy || draftName.trim().length === 0}
            >
              {editing ? "Save" : "Create"}
            </Button>
          </>
        }
      >
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Name</span>
          <TextInput
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder="e.g. Relational Databases"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") submitEdit();
            }}
          />
        </label>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        title="Delete category"
        onClose={() => {
          if (!busy) setDeleteTarget(null);
        }}
        footer={
          <>
            <Button onClick={() => setDeleteTarget(null)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={busy}>
              Delete
            </Button>
          </>
        }
      >
        <p className={styles.confirmText}>
          Delete “{deleteTarget?.name}”? This removes the category term. Existing
          links to it may be affected. This cannot be undone.
        </p>
      </Dialog>
    </div>
  );
}

export default Classification;
